import { useLogCandidateOpened, type CandidateOpenedInput } from "@vidario/api-client";
import { safeHttpUrl } from "@vidario/domain";
import { ConfirmDialog, useToast } from "@vidario/ui";
import { useState, type ReactElement, type ReactNode } from "react";

interface ConfirmOpenButtonProps {
  /** The clear-data URL; never rendered into the page. */
  url: string;
  candidateId: string;
  action: CandidateOpenedInput["action"];
  kind: CandidateOpenedInput["kind"];
  title: string;
  body: string;
  confirmLabel: string;
  className?: string;
  "aria-label"?: string;
  children: ReactNode;
}

/** In the Anonym-Ansicht (#140) a profile or attachment opens only after a
 * confirm, and the opening is logged. A button, not a link: a hovered link
 * shows its URL (with the name) in the status bar. */
export function ConfirmOpenButton({
  url,
  candidateId,
  action,
  kind,
  title,
  body,
  confirmLabel,
  className,
  children,
  ...aria
}: ConfirmOpenButtonProps): ReactElement {
  const [open, setOpen] = useState(false);
  const log = useLogCandidateOpened();
  const toast = useToast();
  const safe = safeHttpUrl(url) ?? (url.startsWith("blob:") ? url : null);
  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => setOpen(true)}
        disabled={!safe}
        {...(aria["aria-label"] ? { "aria-label": aria["aria-label"] } : {})}
      >
        {children}
      </button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={title}
        confirmLabel={confirmLabel}
        initialFocus="cancel"
        onConfirm={() => {
          setOpen(false);
          // Inside the click, so the browser counts it as the user's gesture.
          if (safe) window.open(safe, "_blank", "noopener,noreferrer");
          log.mutate(
            { candidateId, action, kind },
            {
              onError: () =>
                toast.push({
                  kind: "warning",
                  title: LOG_FAILED,
                  message: "Bitte gib dem Team Bescheid.",
                }),
            },
          );
        }}
      >
        {body}
      </ConfirmDialog>
    </>
  );
}

export const LOG_FAILED = "Öffnen wurde nicht protokolliert";

export function attachmentConfirm(label: string): {
  title: string;
  body: string;
  confirmLabel: string;
} {
  return {
    title: `${label} öffnen?`,
    body: "Die Datei enthält den Klarnamen. Wenn du deinen Bildschirm teilst, sehen alle Teilnehmer ihn.",
    confirmLabel: "Öffnen",
  };
}

export const PROFILE_CONFIRM = {
  title: "Originalprofil öffnen?",
  body: "Öffnet das Originalprofil mit Klarnamen. Wenn du deinen Bildschirm teilst, sehen alle Teilnehmer den Namen.",
  confirmLabel: "Profil öffnen",
} as const;
